import { useState, useEffect } from 'react';
import {
  X,
  Search,
  Loader2,
  ChevronRight,
  ChevronDown,
  Check,
  FolderTree,
  Package,
  RefreshCw,
  Database,
  AlertCircle,
  Clock
} from 'lucide-react';
import { productosApi } from '../utils/api';
import clsx from 'clsx';
import toast from 'react-hot-toast';

// Category Tree Item Component
function CategoryTreeItem({ item, level = 0, onSelect, selectedId, expandedIds, onToggle }) {
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedIds.has(item.id);
  const isSelected = selectedId === item.id;

  return (
    <div>
      <button
        onClick={() => {
          if (hasChildren) {
            onToggle(item.id);
          }
          onSelect(item);
        }}
        className={clsx(
          'w-full text-left py-1.5 pr-2 flex items-center gap-1.5 hover:bg-blue-50 rounded transition-colors',
          isSelected && 'bg-blue-100 text-blue-700'
        )}
        style={{ paddingLeft: `${level * 12 + 8}px` }}
      >
        {hasChildren ? (
          isExpanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-gray-500 shrink-0" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-gray-500 shrink-0" />
          )
        ) : (
          <span className="w-3.5" />
        )}
        <span className={clsx(
          'font-mono text-xs shrink-0 w-8 text-right',
          isSelected ? 'text-blue-600 font-semibold' : 'text-blue-500'
        )}>{item.code}</span>
        <span className={clsx(
          'truncate text-xs',
          isSelected ? 'font-medium' : 'text-gray-700'
        )}>{item.name}</span>
      </button>

      {hasChildren && isExpanded && (
        <div>
          {item.children.map((child) => (
            <CategoryTreeItem
              key={child.id}
              item={child}
              level={level + 1}
              onSelect={onSelect}
              selectedId={selectedId}
              expandedIds={expandedIds}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function CabysExplorer({ isOpen, onClose, onSelect }) {
  const [activeTab, setActiveTab] = useState('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Sync status
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Category tree from local DB
  const [categoryTree, setCategoryTree] = useState([]);
  const [loadingTree, setLoadingTree] = useState(false);
  const [needsSync, setNeedsSync] = useState(false);

  // Category state
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryResults, setCategoryResults] = useState([]);
  const [filteredCategoryResults, setFilteredCategoryResults] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loadingCategory, setLoadingCategory] = useState(false);

  const [selectedCabys, setSelectedCabys] = useState(null);

  // Load sync status on mount
  useEffect(() => {
    if (isOpen) {
      loadSyncStatus();
      loadCategoryTree();
    } else {
      // Reset syncing state when closing so next open is clean
      setSyncing(false);
    }
  }, [isOpen]);

  const loadSyncStatus = async () => {
    try {
      const response = await productosApi.getCabysSyncStatus();
      if (response.data?.data) {
        const status = response.data.data;
        const wasRunning = syncStatus?.sync_status === 'running' || syncing;

        setSyncStatus(status);

        if (status.sync_status === 'running') {
          setSyncing(true);
          // Poll for updates more frequently
          setTimeout(loadSyncStatus, 1500);
        } else {
          // Sync is not running
          const justCompleted = wasRunning && status.sync_status === 'completed';

          setSyncing(false);

          // If sync just completed, reload the tree and show toast
          if (justCompleted) {
            toast.success(`Sincronización completada: ${status.total_codes?.toLocaleString() || status.total_in_db?.toLocaleString()} códigos`);
            setNeedsSync(false);
            // Reload the category tree
            loadCategoryTree();
          }

          // Also hide needsSync banner if we have codes
          if (status.total_in_db > 0) {
            setNeedsSync(false);
          }
        }
      }
    } catch (error) {
      console.error('Error loading sync status:', error);
    }
  };

  const loadCategoryTree = async () => {
    setLoadingTree(true);
    try {
      const response = await productosApi.getCabysCategories();
      if (response.data?.data) {
        const tree = response.data.data;
        setCategoryTree(tree);
        // Only show needsSync if explicitly flagged OR no categories at all
        const showNeedsSync = response.data.needsSync === true || tree.length === 0;
        setNeedsSync(showNeedsSync);
      }
    } catch (error) {
      console.error('Error loading CABYS categories:', error);
      setNeedsSync(true);
    } finally {
      setLoadingTree(false);
    }
  };

  const startSync = async () => {
    try {
      setSyncing(true);
      await productosApi.syncCabys();
      toast.success('Sincronización iniciada');
      // Poll for status
      setTimeout(loadSyncStatus, 2000);
    } catch (error) {
      console.error('Error starting sync:', error);
      toast.error('Error al iniciar sincronización');
      setSyncing(false);
    }
  };

  // Toggle category expansion
  const toggleExpanded = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Search with debounce - improved version
  useEffect(() => {
    // Only search when on search tab
    if (activeTab !== 'search') {
      return;
    }

    // Clear results if query is too short
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    // Set searching immediately for UI feedback
    setSearching(true);

    const timer = setTimeout(async () => {
      try {
        const response = await productosApi.searchCabys(searchQuery);
        // Only update if this is still the current query
        setSearchResults(response.data?.data || []);
      } catch (error) {
        console.error('Error searching CABYS:', error);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 400); // Slightly longer debounce for stability

    return () => {
      clearTimeout(timer);
    };
  }, [searchQuery, activeTab]);

  // Load category results from local DB
  const loadCategoryResults = async (category) => {
    setSelectedCategory(category);
    setLoadingCategory(true);
    setCategoryResults([]);
    setFilteredCategoryResults([]);
    setCategoryFilter('');

    try {
      const response = await productosApi.getCabysByCategory(category.code);
      const results = response.data?.data || [];
      setCategoryResults(results);
      setFilteredCategoryResults(results);
    } catch (error) {
      console.error('Error loading category:', error);
    } finally {
      setLoadingCategory(false);
    }
  };

  // Filter category results
  useEffect(() => {
    if (!categoryFilter.trim()) {
      setFilteredCategoryResults(categoryResults);
      return;
    }

    const filter = categoryFilter.toLowerCase();
    const filtered = categoryResults.filter(item =>
      item.codigo.includes(filter) ||
      item.descripcion.toLowerCase().includes(filter)
    );
    setFilteredCategoryResults(filtered);
  }, [categoryFilter, categoryResults]);

  const handleSelect = (cabys) => {
    setSelectedCabys(cabys);
  };

  const confirmSelection = () => {
    if (selectedCabys) {
      onSelect(selectedCabys);
      onClose();
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Nunca';
    return new Date(dateStr).toLocaleString('es-CR');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-[95vw] max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Package className="h-6 w-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Explorador de Códigos CABYS</h2>
              {syncStatus && (
                <p className="text-xs text-gray-500">
                  <Database className="inline h-3 w-3 mr-1" />
                  {syncStatus.total_in_db?.toLocaleString() || 0} códigos en DB local
                  {syncStatus.last_sync && (
                    <span className="ml-2">
                      <Clock className="inline h-3 w-3 mr-1" />
                      Último sync: {formatDate(syncStatus.last_sync)}
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {syncing && syncStatus && syncStatus.progress_total > 0 && (
              <div className="text-sm text-gray-600 mr-2">
                <span className="font-medium">{syncStatus.codes_found?.toLocaleString() || 0}</span> códigos
                <span className="text-gray-400 mx-1">|</span>
                {Math.round((syncStatus.progress_current / syncStatus.progress_total) * 100)}%
              </div>
            )}
            <button
              onClick={startSync}
              disabled={syncing}
              className={clsx(
                'flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors font-medium border-2',
                syncing
                  ? 'border-blue-400 text-blue-600 bg-blue-50'
                  : 'border-blue-500 text-blue-700 hover:bg-blue-50 hover:border-blue-600'
              )}
            >
              <RefreshCw className={clsx('h-4 w-4', syncing && 'animate-spin')} />
              {syncing ? 'Sincronizando...' : 'Sincronizar Ahora'}
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Sync Warning */}
        {needsSync && !syncing && (
          <div className="mx-6 mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-800">Base de datos CABYS vacía</p>
              <p className="text-xs text-yellow-600">Haga clic en "Sincronizar Ahora" para descargar el catálogo completo de Hacienda</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          <button
            onClick={() => {
              setActiveTab('search');
              // Search will re-trigger automatically via useEffect when tab changes
            }}
            className={clsx(
              'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'search'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            <Search className="h-4 w-4" />
            Búsqueda por Términos
          </button>
          <button
            onClick={() => setActiveTab('category')}
            className={clsx(
              'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'category'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            <FolderTree className="h-4 w-4" />
            Buscar por Categoría
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {activeTab === 'search' ? (
            // Search Tab
            <div className="flex-1 flex flex-col p-6">
              <div className="relative mb-4">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar por código o descripción..."
                  className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                  autoFocus
                />
                {searching && (
                  <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-blue-500 animate-spin" />
                )}
              </div>

              <div className="flex-1 overflow-auto border border-gray-200 rounded-lg">
                {searchResults.length > 0 ? (
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-36">Código</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Descripción</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-20">IVA</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {searchResults.map((cabys) => (
                        <tr
                          key={cabys.codigo}
                          onClick={() => handleSelect(cabys)}
                          onDoubleClick={() => { onSelect(cabys); onClose(); }}
                          className={clsx(
                            'cursor-pointer transition-colors',
                            selectedCabys?.codigo === cabys.codigo
                              ? 'bg-blue-50 border-l-4 border-l-blue-500'
                              : 'hover:bg-gray-50'
                          )}
                        >
                          <td className="px-4 py-3">
                            <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                              {cabys.codigo}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm text-gray-900">{cabys.descripcion}</p>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-sm font-medium text-gray-700">{cabys.impuesto}%</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : searchQuery.length >= 2 && !searching ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500">
                    <Package className="h-12 w-12 mb-3 text-gray-300" />
                    <p className="text-lg">No se encontraron resultados</p>
                    {needsSync && <p className="text-sm">Sincronice el catálogo CABYS primero</p>}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <Search className="h-12 w-12 mb-3" />
                    <p>Escriba al menos 2 caracteres para buscar</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Category Tab
            <div className="flex-1 flex overflow-hidden">
              {/* Category Tree */}
              <div className="w-96 min-w-96 border-r border-gray-200 flex flex-col bg-gray-50">
                <div className="px-4 py-3 border-b border-gray-200 bg-white">
                  <h3 className="text-sm font-semibold text-gray-700">Categorías CABYS</h3>
                </div>
                <div className="flex-1 overflow-y-auto p-2">
                  {loadingTree ? (
                    <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                      <Loader2 className="h-6 w-6 animate-spin mb-2" />
                      <p className="text-sm">Cargando...</p>
                    </div>
                  ) : categoryTree.length > 0 ? (
                    categoryTree.map((item) => (
                      <CategoryTreeItem
                        key={item.id}
                        item={item}
                        level={0}
                        onSelect={loadCategoryResults}
                        selectedId={selectedCategory?.id}
                        expandedIds={expandedIds}
                        onToggle={toggleExpanded}
                      />
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center h-32 text-gray-400 text-center p-4">
                      <Database className="h-6 w-6 mb-2" />
                      <p className="text-sm">Sin datos</p>
                      <button
                        onClick={startSync}
                        disabled={syncing}
                        className="text-blue-600 text-sm mt-2 hover:underline"
                      >
                        {syncing ? 'Sincronizando...' : 'Sincronizar desde Hacienda'}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Category Results */}
              <div className="flex-1 flex flex-col p-4 overflow-hidden">
                {selectedCategory ? (
                  <>
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        <span className="font-mono text-blue-600 mr-2">{selectedCategory.code}</span>
                        {selectedCategory.name}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {loadingCategory ? 'Cargando...' : `${filteredCategoryResults.length} de ${categoryResults.length} códigos`}
                      </p>
                    </div>

                    {/* Search within category */}
                    <div className="flex gap-2 mb-4">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          value={categoryFilter}
                          onChange={(e) => setCategoryFilter(e.target.value)}
                          placeholder="Filtrar por código o descripción..."
                          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                      {categoryFilter && (
                        <button
                          onClick={() => setCategoryFilter('')}
                          className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          Limpiar
                        </button>
                      )}
                    </div>

                    <div className="flex-1 overflow-auto border border-gray-200 rounded-lg bg-white">
                      {loadingCategory ? (
                        <div className="flex items-center justify-center h-full">
                          <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                        </div>
                      ) : filteredCategoryResults.length > 0 ? (
                        <table className="w-full table-fixed">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider w-40 border-b border-gray-200">Código</th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">Descripción</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {filteredCategoryResults.map((cabys) => (
                              <tr
                                key={cabys.codigo}
                                onClick={() => handleSelect(cabys)}
                                onDoubleClick={() => { onSelect(cabys); onClose(); }}
                                className={clsx(
                                  'cursor-pointer transition-colors',
                                  selectedCabys?.codigo === cabys.codigo
                                    ? 'bg-blue-50 border-l-4 border-l-blue-500'
                                    : 'hover:bg-gray-50'
                                )}
                              >
                                <td className="px-4 py-2.5">
                                  <code className="font-mono text-sm text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
                                    {cabys.codigo}
                                  </code>
                                </td>
                                <td className="px-4 py-2.5 text-sm text-gray-800">
                                  {cabys.descripcion}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : categoryFilter ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-400">
                          <Search className="h-12 w-12 mb-3" />
                          <p>No hay resultados para "{categoryFilter}"</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full text-gray-400">
                          <Package className="h-12 w-12 mb-3" />
                          <p>No se encontraron códigos</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <FolderTree className="h-12 w-12 mb-3" />
                    <p>Seleccione una categoría para ver los códigos</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="flex-1">
            {selectedCabys && (
              <div className="flex items-center gap-3">
                <Check className="h-5 w-5 text-green-600" />
                <span className="text-sm text-gray-700">
                  Seleccionado: <span className="font-mono font-medium">{selectedCabys.codigo}</span>
                  {' - '}{selectedCabys.descripcion?.substring(0, 50)}...
                </span>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border-2 border-gray-400 rounded-lg hover:bg-gray-50 hover:border-gray-500 transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              onClick={confirmSelection}
              disabled={!selectedCabys}
              className="px-6 py-2 text-green-700 border-2 border-green-500 rounded-lg hover:bg-green-50 hover:border-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
            >
              <Check className="h-4 w-4" />
              Seleccionar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
